import {
    Button,
    DatePickerWithIcon,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from '@/shared/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useEffect } from 'react'

type CustomContractFormValues = { number: string; date: Date }

type CustomContractFormProps = {
    open: boolean
    setOpen: (open: boolean) => void
    onSubmit: (data: CustomContractFormValues) => Promise<void> | void
    mode?: 'create' | 'edit'
    initialValues?: CustomContractFormValues
    isSubmitting?: boolean
}

const CustomContractForm = ({
    open,
    setOpen,
    onSubmit,
    mode = 'create',
    initialValues,
    isSubmitting = false,
}: CustomContractFormProps) => {
    const {
        handleSubmit,
        control,
        formState: { errors, isSubmitting: formSubmitting },
        reset,
    } = useForm<CustomContractFormValues>({
        resolver: zodResolver(
            z.object({
                number: z.string().min(1, 'Номер договора обязателен'),
                date: z.date({
                    message: 'Дата создания обязательна',
                }),
            })
        ),
        defaultValues: {
            number: initialValues?.number ?? '',
            date: initialValues?.date ?? new Date(),
        },
    })

    useEffect(() => {
        if (!open) {
            return
        }

        reset({
            number: initialValues?.number ?? '',
            date: initialValues?.date ?? new Date(),
        })
    }, [open, initialValues, reset])

    const onSubmitForm = handleSubmit(async (data) => {
        await onSubmit(data)
    })

    const submitInProgress = formSubmitting || isSubmitting
    const isEditMode = mode === 'edit'

    const title = isEditMode
        ? 'Редактирование своего договора'
        : 'Добавление своего договора'

    return (
        <Dialog open={open} onOpenChange={setOpen} modal={false}>
            <DialogContent
                aria-describedby={undefined}
                onInteractOutside={(event) => event.preventDefault()}
                onPointerDownOutside={(event) => event.preventDefault()}
                className="left-auto right-16 top-1/3 w-[505px] translate-x-0 translate-y-0 gap-0 overflow-y-auto bg-white px-10 py-[30px] shadow-[0px_4px_24px_0px_rgba(0,0,0,0.15)]"
            >
                <DialogHeader className="w-full justify-center pb-4 text-xs font-black">
                    <DialogTitle className="flex w-full items-center">
                        <div className="flex items-center gap-3">
                            <span className="text-[22px]">{title}</span>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={onSubmitForm} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Номер договора</Label>
                        <Controller
                            control={control}
                            name="number"
                            render={({ field }) => (
                                <Input
                                    placeholder="Введите номер"
                                    {...field}
                                    error={errors.number?.message}
                                />
                            )}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Дата создания</Label>
                        <Controller
                            control={control}
                            name="date"
                            render={({ field }) => (
                                <DatePickerWithIcon
                                    selected={field.value}
                                    onChange={(date) =>
                                        field.onChange(date ?? field.value)
                                    }
                                    dateFormat="dd.MM.yy"
                                    dropdownMode="scroll"
                                    className="text-xs"
                                    maxDate={new Date()}
                                />
                            )}
                        />
                    </div>

                    <p className="text-xs leading-4 text-gray-400">
                        Сервис пока не поддерживает работу по рамочным договорам
                    </p>

                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setOpen(false)}
                            className="flex-1"
                            disabled={submitInProgress}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1"
                            disabled={submitInProgress}
                        >
                            {isEditMode ? 'Сохранить' : 'Добавить'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default CustomContractForm
